import {
  ConditionGroupLogic,
  isConditionGroup,
  isRelatedResourceCondition,
  type ConditionGroupItem,
  type FieldCondition,
} from '@reach/shared-types/conditions';
import React from 'react';
import styles from './SegmentVisualBuilder.module.css';

interface DescriptionAccordionNodeProps {
  item: ConditionGroupItem;
}

// Related-resource sub-conditions are always combined with AND.
const RELATED_RESOURCE_CONNECTOR = ConditionGroupLogic.AND.toUpperCase();

function formatConditionValue(value: FieldCondition['value'][number]): string {
  let formatted: string;
  if (value === null || typeof value !== 'object') {
    formatted = String(value);
  } else if (value instanceof Date) {
    formatted = value.toLocaleDateString();
  } else if ('type' in value && value.type === 'relative') {
    formatted = `${value.value} ${value.unit} ${value.direction}`;
  } else if ('startDate' in value && 'endDate' in value) {
    formatted = `${formatConditionValue(value.startDate)} – ${formatConditionValue(value.endDate)}`;
  } else {
    formatted = String(value);
  }
  return formatted;
}

const AccordionRow: React.FC<{ description?: string; children: React.ReactNode }> = ({
  description,
  children,
}) => (
  <details className={styles.accordionNode}>
    <summary className={styles.accordionSummary}>
      {description ? (
        description
      ) : (
        <span className={styles.descriptionMissing}>description missing</span>
      )}
    </summary>
    {children}
  </details>
);

export const DescriptionAccordionNode: React.FC<DescriptionAccordionNodeProps> = ({ item }) => {
  if (isConditionGroup(item) || isRelatedResourceCondition(item)) {
    const connectorLabel = isConditionGroup(item)
      ? item.logic.toUpperCase()
      : RELATED_RESOURCE_CONNECTOR;
    return (
      <AccordionRow description={item.description}>
        <div className={styles.accordionChildren}>
          {item.conditions.map((child, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className={styles.accordionConnector}>{connectorLabel}</div>}
              <DescriptionAccordionNode item={child} />
            </React.Fragment>
          ))}
        </div>
      </AccordionRow>
    );
  }

  const operatorLabel = item.operator.replace(/_/g, ' ');
  const valueLabel = item.value.map(formatConditionValue).join(', ');
  return (
    <AccordionRow description={item.description}>
      <div className={styles.accordionLeafBody}>
        <span className={styles.leafField}>{item.field}</span>
        <span className={styles.leafOperator}>{operatorLabel}</span>
        {valueLabel && <span className={styles.leafValue}>{valueLabel}</span>}
      </div>
    </AccordionRow>
  );
};
